import { PlacementXAnchor, PlacementYAnchor, TextAlignment } from "building-types/annotation/annotationTypesEnum";
import {
  defaultPlacementXAnchor,
  defaultPlacementYAnchor,
  type SheetTextGeometry,
  type StyledSheetTextGeometry,
} from "building-types/views/sheetGeometryTypes";
import { BUILT_IN_VIEW_SCALE_1_PER_4_INCH } from "building-types/views/viewScaleConstants";
import { ONE_INCH } from "common/constants/scaleFactors";
import type { NominalNumber, WorldInches } from "common/types/nominalNumbersTypes";
import { unsignedDeltaRadians } from "common/utils/math";
import {
  nominalNumberNew,
  numAdd,
  numDiv,
  numMul,
  numSub,
  sheetInchesToWorld,
  sheetPointsToInches,
} from "common/utils/nominalNumbers";
import { TextVerticalAlignment } from "common/utils/textAlignment";
import type { AABB2 } from "geom/aabb2Types";
import { DIST_EPSILON, HALF_PI } from "geom/core";
import type { OBB2 } from "geom/obb2";
import { obb2New } from "geom/obb2";
import {
  polyLine2AABB2,
  polyLine2Edge,
  polyLine2EdgeLength,
  polyLine2EdgeTangent,
  polyLine2Rotate,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { PDFTextMetricsProvider } from "pdf/pdfTextMetricsProvider";
import { fonts, STANDARD_FONT_SIZE } from "pdf/planFonts";
import styles from "../planStyles";
import { resolveSheetGeometryStyle } from "./primitiveStyles";

export function transformTextGeometryForInvertedY(geometry: SheetTextGeometry): SheetTextGeometry {
  const placementYAnchor = flipPlacementYAnchor(geometry.placementYAnchor ?? defaultPlacementYAnchor);
  const verticalAlignment = flipVerticalAlignment(geometry.verticalAlignment ?? styles.text.verticalAlignment);
  const boxVerticalAlignmentPercentageOffset = -(
    geometry.boxVerticalAlignmentPercentageOffset ?? styles.text.boxVerticalAlignmentPercentageOffset
  );
  return {
    ...geometry,
    placementYAnchor,
    verticalAlignment,
    boxVerticalAlignmentPercentageOffset,
  };
}

// Expects the text geometry to be normalized and styled
export function planTextGeometryOBB(geometry: SheetTextGeometry): OBB2 {
  /**
   * Sheets have a coordinate system of y-up, but all sheet geometry
   * transformations happen in y-down space. we need to flip the vertical
   * styles to match the coordinate system of the sheet.
   */
  const perimeter = planTextBoxPerimeter(transformTextGeometryForInvertedY(geometry));

  const edgeU = polyLine2Edge(perimeter, 0);
  const edgeV = polyLine2Edge(perimeter, 1);

  const halfWidth = polyLine2EdgeLength(edgeU, 0) / 2;
  const halfHeight = polyLine2EdgeLength(edgeV, 0) / 2;

  if (halfHeight < DIST_EPSILON || halfWidth < DIST_EPSILON) {
    return obb2New(edgeU[0], edgeU[1], 0, 0, 0);
  }

  const [ux, uy] = polyLine2EdgeTangent(edgeU, 0);
  const [vx, vy] = polyLine2EdgeTangent(edgeV, 0);

  const rotationRad = Math.atan2(uy, ux);
  const centerX = edgeU[0] + halfWidth * ux + halfHeight * vx;
  const centerY = edgeU[1] + halfWidth * uy + halfHeight * vy;

  return obb2New(centerX, centerY, halfWidth, halfHeight, rotationRad);
}

// Expects the text geometry to be normalized and styled
export function planTextGeometryAABB(geometry: SheetTextGeometry): AABB2 {
  return polyLine2AABB2(planTextBoxPerimeter(geometry));
}

// Expects the text geometry to be normalized and styled
export function planTextBoxPerimeter(geometry: SheetTextGeometry): PolyLine2 {
  // Ideally we could use the styled type without an "as" cast.
  const resolvedText = resolveSheetGeometryStyle(geometry) as StyledSheetTextGeometry;
  const { width, height } = PDFTextMetricsProvider.instance.measureText(resolvedText);
  const {
    x,
    y,
    alignment,
    verticalAlignment,
    boxWidth,
    boxHeight,
    placementXAnchor = defaultPlacementXAnchor,
    placementYAnchor = defaultPlacementYAnchor,
    boxVerticalAlignmentPercentageOffset = 0,
    rotation = 0,
    strokeWidth,
  } = resolvedText;

  const textBoxWidth = boxWidth ?? width;
  const textBoxHeight = boxHeight ?? height;

  const anchorX = textPlacementOffsetX(textBoxWidth, placementXAnchor);
  const alignmentX = boxWidth ? textAlignmentOffsetX(numSub(boxWidth, width), alignment) : 0;
  const left = x + anchorX - alignmentX;

  const yAnchor = textPlacementOffsetY(textBoxHeight, placementYAnchor, -boxVerticalAlignmentPercentageOffset);
  const yOffset = yAnchor + textAlignmentOffsetY(numSub(height, textBoxHeight), verticalAlignment);

  let top = y + yOffset;
  if (geometry.flipped) {
    top = y - yOffset;
  }

  let bottom = top + height;
  if (geometry.flipped) {
    bottom = top - height;
  }

  // Stroke width is centered on the glyph outline, so only half extends outward.
  const outlinePadding = strokeWidth ? sheetPointsToInches(strokeWidth) / 2 : 0;
  const paddedLeft = left - outlinePadding;
  const paddedRight = left + width + outlinePadding;
  const paddedTop = Math.min(top, bottom) - outlinePadding;
  const paddedBottom = Math.max(top, bottom) + outlinePadding;

  const textPerimeter = polyLine2Rotate(
    polyLine2New([
      paddedLeft,
      paddedTop,
      paddedRight,
      paddedTop,
      paddedRight,
      paddedBottom,
      paddedLeft,
      paddedBottom,
      paddedLeft,
      paddedTop,
    ]),
    rotation,
    x,
    y,
  );

  return textPerimeter;
}

/**
 * @deprecated as many uses of this should be removed as possible
 */
export function textGeometryInBuiltInView(geometry: SheetTextGeometry): SheetTextGeometry {
  const scaleFactor = numDiv(ONE_INCH, BUILT_IN_VIEW_SCALE_1_PER_4_INCH);
  return flipTextGeometry({
    ...geometry,
    fontSize: numMul(geometry.fontSize ?? STANDARD_FONT_SIZE, scaleFactor),
    boxWidth: geometry.boxWidth ? numMul(geometry.boxWidth, scaleFactor) : undefined,
    boxHeight: geometry.boxHeight ? numMul(geometry.boxHeight, scaleFactor) : undefined,
  });
}

export function flipTextGeometry(geometry: SheetTextGeometry): SheetTextGeometry {
  return {
    ...geometry,
    flipped: !(geometry.flipped ?? false),
  };
}

/**
 * @deprecated as many uses of this should be removed as possible, and the above uses should
 * be swapped out instead. this is mainly used for internal plan collision
 */
export function textSizeInView(
  text: string,
  fontSize = STANDARD_FONT_SIZE,
  viewScaleFactor = BUILT_IN_VIEW_SCALE_1_PER_4_INCH,
): [WorldInches, WorldInches] {
  const { width, height } = PDFTextMetricsProvider.instance.measureText({
    text,
    fontSize,
    font: fonts.planSetRegular,
  });
  return [sheetInchesToWorld(width, viewScaleFactor), sheetInchesToWorld(height, viewScaleFactor)];
}

export function textPlacementOffsetX<T extends NominalNumber>(width: T, placementXAnchor = defaultPlacementXAnchor): T {
  let xOffset = nominalNumberNew<T>(0);

  switch (placementXAnchor) {
    case PlacementXAnchor.LEFT: {
      break;
    }
    case PlacementXAnchor.CENTER: {
      xOffset = numMul(width, -0.5);
      break;
    }
    case PlacementXAnchor.RIGHT: {
      xOffset = numMul(width, -1);
      break;
    }
  }

  return xOffset;
}

export function textAlignmentOffsetX<T extends NominalNumber>(width: T, alignment = styles.text.alignment): T {
  let xOffset = nominalNumberNew<T>(0);

  switch (alignment) {
    case TextAlignment.LEFT: {
      break;
    }
    case TextAlignment.CENTER: {
      xOffset = numMul(width, -0.5);
      break;
    }
    case TextAlignment.RIGHT: {
      xOffset = numMul(width, -1);
      break;
    }
  }

  return xOffset;
}

export function textPlacementOffsetY<T extends NominalNumber>(
  height: T,
  placementYAnchor = defaultPlacementYAnchor,
  boxVerticalAlignmentPercentageOffset = 0,
): T {
  let yOffset = nominalNumberNew<T>(0);

  switch (placementYAnchor) {
    case PlacementYAnchor.TOP: {
      break;
    }
    case PlacementYAnchor.CENTER: {
      yOffset = numMul(height, -0.5);
      break;
    }
    case PlacementYAnchor.BOTTOM: {
      yOffset = numMul(height, -1);
      break;
    }
  }

  if (boxVerticalAlignmentPercentageOffset) {
    yOffset = numAdd(yOffset, numMul(height, boxVerticalAlignmentPercentageOffset));
  }

  return yOffset;
}

export function textAlignmentOffsetY<T extends NominalNumber>(
  height: T,
  verticalAlignment = styles.text.verticalAlignment,
): T {
  switch (verticalAlignment) {
    case TextVerticalAlignment.TOP: {
      return nominalNumberNew<T>(0);
    }
    case TextVerticalAlignment.CENTER: {
      return numMul(height, -0.5);
    }
    case TextVerticalAlignment.BOTTOM: {
      return numMul(height, -1);
    }
  }
}

export function normalizeTextBox(geom: SheetTextGeometry, planRotationRads = 0): SheetTextGeometry {
  const textRotationInPlan = (geom.rotation ?? 0) - planRotationRads;
  const { flipped } = normalizeTextRotation(textRotationInPlan, 0);
  return { ...geom, flipped, rotation: textRotationInPlan };
}

export function flipPlacementYAnchor(placementYAnchor = defaultPlacementYAnchor): PlacementYAnchor {
  switch (placementYAnchor) {
    case PlacementYAnchor.TOP:
      return PlacementYAnchor.BOTTOM;
    case PlacementYAnchor.CENTER:
      return PlacementYAnchor.CENTER;
    case PlacementYAnchor.BOTTOM:
      return PlacementYAnchor.TOP;
  }
}

export function flipVerticalAlignment(verticalAlignment = styles.text.verticalAlignment): TextVerticalAlignment {
  switch (verticalAlignment) {
    case TextVerticalAlignment.TOP:
      return TextVerticalAlignment.BOTTOM;
    case TextVerticalAlignment.BOTTOM:
      return TextVerticalAlignment.TOP;
    default:
      return verticalAlignment;
  }
}

export function flipPlacementXAnchor(placementXAnchor = defaultPlacementXAnchor): PlacementXAnchor {
  switch (placementXAnchor) {
    case PlacementXAnchor.LEFT:
      return PlacementXAnchor.RIGHT;
    case PlacementXAnchor.CENTER:
      return PlacementXAnchor.CENTER;
    case PlacementXAnchor.RIGHT:
      return PlacementXAnchor.LEFT;
  }
}

export function normalizeTextRotation(rotation: number, planRotationRads: number) {
  // If the text is very close to axis aligned, we align it.
  let rc = rotation / HALF_PI;

  if (Math.abs(Math.round(rc) - rc) < DIST_EPSILON * 2) {
    rc = Math.round(rc);
    rotation = rc * HALF_PI;
  }

  let flipped = false;

  // If the text is upside down, rotate it 180 degrees
  if (unsignedDeltaRadians(planRotationRads - rotation, Math.PI) < HALF_PI * (1 - DIST_EPSILON)) {
    rotation += Math.PI;
    flipped = true;
  }

  // If the text is rotated exactly 90 degrees, rotate it 180 degrees
  if (unsignedDeltaRadians(planRotationRads - rotation, HALF_PI) < HALF_PI * DIST_EPSILON) {
    rotation += Math.PI;
    flipped = true;
  }
  return { rotation, flipped };
}

export function getTextCenterFromTextBox(textGeometry: StyledSheetTextGeometry): { x: number; y: number } {
  const { width, height } = PDFTextMetricsProvider.instance.measureText(textGeometry);

  const textBoxWidth = textGeometry.boxWidth ?? width;
  const textBoxHeight = textGeometry.boxHeight ?? height;

  const xAnchor = textPlacementOffsetX(textBoxWidth, textGeometry.placementXAnchor);
  const xOffset = xAnchor + textAlignmentOffsetX(numSub(width, textBoxWidth), textGeometry.alignment);

  const verticalOffset = textGeometry.boxVerticalAlignmentPercentageOffset
    ? -textGeometry.boxVerticalAlignmentPercentageOffset
    : undefined;

  const yAnchor = textPlacementOffsetY(textBoxHeight, textGeometry.placementYAnchor, verticalOffset);
  const yOffset = yAnchor + textAlignmentOffsetY(numSub(height, textBoxHeight), textGeometry.verticalAlignment);

  return { x: xOffset + width / 2, y: yOffset + height / 2 };
}
